import { Link } from 'react-router-dom';
import { ArrowRight, Scale, Users, Briefcase, GraduationCap, FileCheck, Banknote, Star, CheckCircle } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import SEOHead from '../components/SEOHead';

const HomePage = () => {
  const { login } = useAuth();

  const services = [
    {
      icon: Scale,
      title: 'Expertise Juridique',
      description: 'Conseil en droit des affaires, contrats, et conformité réglementaire pour sécuriser votre entreprise.'
    },
    {
      icon: Briefcase,
      title: 'Création d\'Entreprise',
      description: 'Accompagnement complet pour structurer et lancer votre projet entrepreneurial au Sénégal.'
    },
    {
      icon: Users,
      title: 'Conseil Stratégique',
      description: 'Vision et stratégie sur mesure pour faire prospérer votre entreprise durablement.'
    }
  ];

  const threeFs = [
    {
      letter: 'F',
      title: 'Formation',
      description: 'Acquérir les compétences essentielles pour bien structurer votre activité.',
      color: 'bg-[#006F33]',
      icon: GraduationCap
    },
    {
      letter: 'F',
      title: 'Formalisation',
      description: 'Accompagnement dans toutes les démarches administratives et juridiques.',
      color: 'bg-[#E6C200]',
      textColor: 'text-[#1A1A1A]',
      icon: FileCheck
    },
    {
      letter: 'F',
      title: 'Financement',
      description: 'Trouver les meilleures options pour soutenir et développer votre entreprise.',
      color: 'bg-[#C0392B]',
      icon: Banknote
    }
  ];

  const testimonials = [
    {
      name: 'Amadou Diallo',
      role: 'Fondateur, Tech Dakar',
      content: 'Teranga Global Legal m\'a accompagné de A à Z dans la création de ma startup. Leur expertise juridique et leurs conseils en financement ont été déterminants.',
      avatar: 'AD'
    },
    {
      name: 'Fatou Seck',
      role: 'Directrice, Seck Consulting',
      content: 'Une équipe professionnelle et à l\'écoute. La méthode des 3F m\'a permis de structurer mon activité de manière optimale.',
      avatar: 'FS'
    },
    {
      name: 'Ibrahima Ndiaye',
      role: 'CEO, AgroSen',
      content: 'Grâce à Teranga Global Legal, j\'ai pu lever des fonds et sécuriser juridiquement mon entreprise. Je les recommande vivement.',
      avatar: 'IN'
    }
  ];

  const whyChooseUs = [
    'Approche complète avec la méthode des 3F',
    'Expertise locale et standards internationaux',
    'Accompagnement personnalisé',
    'Réseau de partenaires influents',
    'Impact social et économique'
  ];

  return (
    <Layout>
      <SEOHead 
        title="Teranga Global Legal"
        description="Cabinet de conseil juridique et d'affaires à Thiès, Sénégal. Accompagnement complet des entrepreneurs avec la méthode des 3F : Formation, Formalisation, Financement."
        path="/"
      />
      {/* Hero Section */}
      <section className="gradient-hero py-20 md:py-32" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-[#006F33]/10 text-[#006F33] px-4 py-2 rounded-full text-sm font-medium">
                <Star className="w-4 h-4" />
                <span>Cabinet de Consultation Leader au Sénégal</span>
              </div>
              
              <h1 className="hero-title" data-testid="hero-title">
                Votre avenir,<br />
                <span className="text-[#006F33]">notre expertise</span>
              </h1>
              
              <p className="hero-subtitle max-w-lg">
                Chez Teranga Global Legal, nous transformons vos ambitions entrepreneuriales en réalités. 
                Expertise juridique et accompagnement stratégique pour votre succès au Sénégal.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="btn-primary flex items-center gap-2" data-testid="cta-rdv">
                    Prendre rendez-vous
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button className="btn-outline" data-testid="cta-services">
                    Découvrir nos services
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative animate-fade-in animation-delay-200">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1631194019419-89ef6ec875db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxhZnJpY2FuJTIwYnVzaW5lc3MlMjB0ZWFtJTIwbWVldGluZyUyMG1vZGVybiUyMG9mZmljZXxlbnwwfHx8fDE3NzIxMzM4Mzl8MA&ixlib=rb-4.1.0&q=85"
                  alt="Équipe Teranga Global Legal"
                  className="w-full h-[400px] md:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
              
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl max-w-xs hidden md:block">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#006F33]/10 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-[#006F33]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1A1A1A]">57 entreprises</p>
                    <p className="text-sm text-slate-500">accompagnées avec succès</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-[#006F33] uppercase tracking-wider">Nos Services</span>
            <h2 className="section-title mt-4">Comment pouvons-nous vous aider ?</h2>
            <p className="section-subtitle mt-4 max-w-2xl mx-auto">
              Une expertise complète pour accompagner les entrepreneurs à chaque étape de leur parcours.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index} 
                className="service-card animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`service-card-${index}`}
              >
                <div className="service-icon">
                  <service.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-[#1A1A1A]">{service.title}</h3>
                <p className="text-slate-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/services">
              <Button className="btn-outline flex items-center gap-2 mx-auto" data-testid="view-all-services">
                Voir tous nos services
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 3F Section */}
      <section className="py-20 md:py-32 gradient-brand" data-testid="threef-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-[#006F33] uppercase tracking-wider">Notre Méthode</span>
            <h2 className="section-title mt-4">Les 3F du Succès</h2>
            <p className="section-subtitle mt-4 max-w-2xl mx-auto">
              Une approche structurée et complète pour transformer votre projet en entreprise prospère.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {threeFs.map((f, index) => (
              <div 
                key={index}
                className={`f-card ${f.color} ${f.textColor || 'text-white'}`}
                data-testid={`threef-card-${f.title.toLowerCase()}`}
              >
                <div className="flex items-center gap-4 mb-6">
                  <span className={`text-6xl font-bold opacity-30`}>{f.letter}</span>
                  <f.icon className={`w-10 h-10 ${f.textColor || 'text-white'}`} />
                </div>
                <h3 className="text-2xl font-bold mb-3">{f.title}</h3>
                <p className={`leading-relaxed ${f.textColor ? 'text-slate-700' : 'text-white/90'}`}>
                  {f.description}
                </p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/les-3f">
              <Button className="btn-primary flex items-center gap-2 mx-auto" data-testid="learn-more-3f">
                En savoir plus sur les 3F
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-semibold text-[#006F33] uppercase tracking-wider">Pourquoi Nous Choisir</span>
              <h2 className="section-title mt-4">Votre succès est notre priorité</h2>
              <p className="section-subtitle mt-4">
                Nous ne vendons pas un service, nous offrons une opportunité de réussir.
              </p>
              
              <ul className="mt-8 space-y-4">
                {whyChooseUs.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#006F33] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
              
              <div className="mt-10">
                <Link to="/a-propos">
                  <Button className="btn-outline flex items-center gap-2" data-testid="learn-more-about">
                    En savoir plus
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1758520145175-aa3b593b81af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxhZnJpY2FuJTIwYnVzaW5lc3MlMjB0ZWFtJTIwbWVldGluZyUyMG1vZGVybiUyMG9mZmljZXxlbnwwfHx8fDE3NzIxMzM4Mzl8MA&ixlib=rb-4.1.0&q=85"
                alt="Équipe Teranga Global Legal"
                className="rounded-2xl shadow-xl w-full h-[450px] object-cover"
              />
              
              {/* Stats Card */}
              <div className="absolute -bottom-8 -right-8 bg-[#006F33] text-white p-8 rounded-2xl shadow-xl hidden md:block">
                <p className="text-4xl font-bold">2</p>
                <p className="text-white/80">années d'expertise</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 md:py-32 bg-[#F9F9F7]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-[#006F33] uppercase tracking-wider">Témoignages</span>
            <h2 className="section-title mt-4">Ce que disent nos clients</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className="testimonial-card"
                data-testid={`testimonial-${index}`}
              >
                <p className="text-slate-600 leading-relaxed mb-6 mt-6">
                  "{testimonial.content}"
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#006F33] flex items-center justify-center text-white font-bold">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <p className="font-semibold text-[#1A1A1A]">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#006F33]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Prêt à concrétiser votre projet ?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Prenez les devants avec Teranga Global Legal. Contactez-nous pour une consultation gratuite.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-[#006F33] hover:bg-slate-100 shadow-lg px-8 py-3 rounded-full font-medium" data-testid="cta-contact">
                Nous contacter
              </Button>
            </Link>
            <Button onClick={login} className="btn-secondary" data-testid="cta-login">
              Se connecter
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
